import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 规范化时区名称
 * 将 UTC-7, UTC+8 等格式转换为 dayjs 可识别的格式
 * @param timezone 时区字符串
 * @returns 规范化后的时区字符串
 */
export const normalizeTimezone = (timezone: string): string => {
    if (!timezone) return 'UTC';
    
    // 如果是 UTC-X 或 UTC+X 格式，转换为正确的格式
    const utcOffsetMatch = timezone.match(/^UTC([+-]\d+)$/);
    if (utcOffsetMatch) {
        const offset = parseInt(utcOffsetMatch[1]);
        // 转换为 +HH:mm 或 -HH:mm 格式
        const sign = offset >= 0 ? '+' : '-';
        const absOffset = Math.abs(offset);
        return `${sign}${absOffset.toString().padStart(2, '0')}:00`;
    }
    
    // 如果是 GMT-X 或 GMT+X 格式，转换为正确的格式
    const gmtOffsetMatch = timezone.match(/^GMT([+-]\d+)$/);
    if (gmtOffsetMatch) {
        const offset = parseInt(gmtOffsetMatch[1]);
        // 转换为 +HH:mm 或 -HH:mm 格式
        const sign = offset >= 0 ? '+' : '-';
        const absOffset = Math.abs(offset);
        return `${sign}${absOffset.toString().padStart(2, '0')}:00`;
    }
    
    // 如果已经是标准格式，直接返回
    return timezone;
};

/**
 * 安全地获取指定时区的当前时间
 * @param timezone 时区字符串
 * @returns dayjs 对象
 */
export const getCurrentTimeInTimezone = (timezone: string): dayjs.Dayjs => {
    const normalizedTimezone = normalizeTimezone(timezone);
    
    try {
        return dayjs().tz(normalizedTimezone);
    } catch (tzError) {
        console.warn('Invalid timezone format, falling back to UTC:', normalizedTimezone);
        return dayjs().utc();
    }
};

/**
 * 安全地获取指定时区的今天开始时间
 * @param timezone 时区字符串
 * @returns dayjs 对象
 */
export const getTodayInTimezone = (timezone: string): dayjs.Dayjs => {
    return getCurrentTimeInTimezone(timezone).startOf('day');
};

/**
 * 检查当前时间是否在指定日期的指定时间前（基于指定时区）
 * @param timezone 时区字符串
 * @param targetDate 目标日期字符串 (YYYY-MM-DD格式)
 * @param hour 目标小时（0-23）
 * @param minute 目标分钟（0-59），默认为0
 * @returns 如果当前时间在目标日期的指定时间前返回true，否则返回false
 */
export const isBeforeTime = (timezone: string, targetDate: string, hour: number, minute: number = 0): boolean => {
    if (!timezone || !targetDate) {
        return false;
    }

    try {
        // 获取当前时间在指定时区的时间
        const currentTimeInTimezone = getCurrentTimeInTimezone(timezone);
        
        // 获取目标日期在指定时区的指定时间
        const targetDateInTimezone = dayjs.tz(targetDate, normalizeTimezone(timezone));
        const targetTimeInTimezone = targetDateInTimezone.hour(hour).minute(minute).second(0).millisecond(0);
        
        // 比较当前时间是否在指定时间前
        return currentTimeInTimezone.isBefore(targetTimeInTimezone);
    } catch (error) {
        console.error('Error checking time before specified time:', error);
        return false;
    }
};